import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, XIcon } from 'lucide-react'
import { useRegisterModal } from '@/context/ModalContext'
import { cn } from '@/lib/utils'

interface UnsavedChangesDialogProps {
  open: boolean
  onDiscard: () => void
  onSave: () => void
  onCancel: () => void
}

// Z-index must be above z-fullscreen (350) so the dialog renders on top of the overlay.
// The standard DialogContent uses z-modal (200) which is below z-fullscreen.
const Z_ABOVE_FULLSCREEN = 360

export function UnsavedChangesDialog({ open, onDiscard, onSave, onCancel }: UnsavedChangesDialogProps) {
  useRegisterModal(open, onCancel)

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogPortal>
        <DialogOverlay className="!z-[360]" />
        <DialogPrimitive.Content
          className={cn(
            'popover-styled fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
            'grid w-full max-w-[calc(100%-2rem)] sm:max-w-md gap-4 p-6 outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-200'
          )}
          style={{ zIndex: Z_ABOVE_FULLSCREEN }}
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <DialogTitle>Unsaved Changes</DialogTitle>
            </div>
            <DialogDescription>
              You have unsaved changes to this file. Would you like to save them before closing?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={onDiscard}>
              Discard
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave}>
              Save
            </Button>
          </DialogFooter>
          <DialogPrimitive.Close
            className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
